import React, { useRef, useEffect } from 'react';
import { BottomSheet, BottomSheetRef } from 'react-spring-bottom-sheet';
// eslint-disable-next-line import/no-unassigned-import
import 'react-spring-bottom-sheet/dist/style.css';
import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import colors from '../../../../themes/main-colors';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerContainer: {
      '& [data-rsbs-overlay]': {
        borderTopLeftRadius: '10px',
        borderTopRightRadius: '10px',
        overflow: 'hidden',
      },
      '& [data-rsbs-backdrop]': {
        backgroundColor: 'rgba(0, 0, 0, 0)',
        zIndex: -1,
      },
      '& [data-rsbs-footer]': {
        padding: theme.spacing(0, 0, 0.5, 0),
        boxShadow: 'none',
        textAlign: 'center',
        background: colors.white,
        opacity: '0.9',
      },
      '& [data-rsbs-header]': {
        display: 'none',
      },
      '&.slideNumber0': {
        '& [data-rsbs-header]': {
          display: 'block',
        },
      },
      '& [data-rsbs-scroll]': {
        background:
          'linear-gradient(white 30%, rgba(255, 255, 255, 0)), linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%, radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, .2), rgba(0, 0, 0, 0)), radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, .2), rgba(0, 0, 0, 0)) 0 100%',
        backgroundRepeat: 'no-repeat',
        backgroundColor: 'white',
        backgroundSize: '100% 40px, 100% 40px, 100% 14px, 100% 14px',
        backgroundAttachment: 'local, local, scroll, scroll',
      },
    },
    drawerOverlayContainer: {
      zIndex: 3,
    },
    drawerContent: {
      padding: '1rem',
    },
    drawerScrollShadow: {
      position: 'fixed',
      left: 0,
      right: 0,
      zIndex: 99,
      height: '10px',
    },
    drawerScrollShadowTop: {
      top: '18px',
      background:
        'radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, .2), rgba(0, 0, 0, 0))',
      opacity: 0,
    },
    drawerScrollShadowBottom: {
      bottom: 0,
      background:
        'radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, .2), rgba(0, 0, 0, 0))',
      opacity: 1,
    },
    drawerWithFooter: {
      '& [data-rsbs-content]': {
        // position: 'absolute',
        top: 20,
        bottom: 0,
        right: 0,
        left: 0,
        paddingBottom: '100px',
        overflowY: 'scroll',
      },
      '& $drawerScrollShadowBottom': {
        bottom: '79px',
      },
    },
  }),
);

export enum DrawerSnapType {
  default = 'default',
  maxHeight = 'maxHeight',
  minHeight = 'minHeight',
}

type Props = {
  children?: React.ReactNode;
  isOpen?: boolean;
  overlay?: boolean;
  onClose?: () => void;
  defaultSnap?: DrawerSnapType;
  refId?: number;
  footer?: React.ReactNode;
};

type PanelType = {
  id: number;
  sheetRef: React.RefObject<BottomSheetRef>;
};

const panel: PanelType[] = [];

export default function BottomDrawerLayout(props: Props) {
  const classes = useStyles();

  const {
    children,
    isOpen = true,
    onClose,
    overlay = false,
    defaultSnap = DrawerSnapType.default,
    refId = Math.random(),
    footer,
  } = props;
  const sheetRef = useRef<BottomSheetRef>(null);
  const DEFAULT_MIN_HEIGHT = 30;
  const OFFSET_MAX_HEIGHT = 230;
  const [open, setOpen] = React.useState<boolean>(isOpen);
  const idRef = useRef(refId);

  useEffect(() => {
    removeNullSheetRef();
    if (idRef.current === 0 && panel.length === 1) {
      requestAnimationFrame(() => {
        sheetRef.current?.snapTo(({ minHeight, maxHeight }) =>
          getDefaultSnapHeight(defaultSnap, minHeight, maxHeight),
        );
      });
    }
  }, [children]);

  const getDefaultSnapHeight = (
    snapType: DrawerSnapType,
    minHeight: number,
    maxHeight: number,
  ) => {
    if (snapType === DrawerSnapType.maxHeight) {
      return maxHeight - OFFSET_MAX_HEIGHT;
    } else if (snapType === DrawerSnapType.minHeight) {
      return DEFAULT_MIN_HEIGHT;
    } else {
      return minHeight > maxHeight - OFFSET_MAX_HEIGHT
        ? maxHeight - OFFSET_MAX_HEIGHT
        : minHeight;
    }
  };

  const closePanel = () => {
    if (idRef.current !== 0) {
      setOpen(false);
      panel.splice(
        panel.findIndex(p => p.id === idRef.current),
        1,
      );
    }
  };
  const clearPreviousPanelData = () => {
    if (idRef.current !== 0) {
      return;
    }

    const defaultPanel = panel.find(p => p.id === 0);
    if (Boolean(defaultPanel)) {
      panel.splice(0, panel.length);
      defaultPanel && panel.push(defaultPanel);
    }
  };

  const removeNullSheetRef = () => {
    const validPanels = panel.filter(p => Boolean(p.sheetRef.current));
    if (validPanels.length > 0) {
      panel.splice(0, panel.length);
      validPanels.forEach(validPanel => {
        panel.push(validPanel);
      });
    }
  };
  const triggerCloseOtherPanelAndHideMainPanel = () => {
    panel.forEach(p => {
      if (p.id !== idRef.current && p.id !== 0) {
        requestAnimationFrame(() =>
          p.sheetRef.current?.snapTo(0, { source: 'snap-to-bottom' }),
        );
      }
      if (
        p.id === 0 &&
        p.id !== idRef.current &&
        Number(p.sheetRef.current?.height) > 200
      ) {
        requestAnimationFrame(() => p.sheetRef.current?.snapTo(20));
      }
    });
  };

  const replaceNullDefaultPanel = () => {
    const defaultPanel = panel.find(p => p.id === 0);
    if (Boolean(defaultPanel?.sheetRef && idRef.current === 0)) {
      panel.splice(
        panel.findIndex(p => p.id === 0),
        1,
      );
      saveCurrentPanel();
    }
  };

  const saveCurrentPanel = () => panel.push({ id: idRef.current, sheetRef });
  useEffect(() => {
    if (
      defaultSnap !== DrawerSnapType.default &&
      sheetRef &&
      sheetRef.current
    ) {
      sheetRef.current.snapTo(({ minHeight, maxHeight }) =>
        getDefaultSnapHeight(defaultSnap, minHeight, maxHeight),
      );
    }
  }, [defaultSnap]);

  const handleScroll = (e: any) => {
    if (e.target.hasAttribute('data-rsbs-content')) {
      const offsetTop = 10;
      const scrollElement = e.target;
      const drawerScrollShadowTop: any = document.querySelector(
        `.${classes.drawerScrollShadow}.${classes.drawerScrollShadowTop}`,
      );
      const drawerScrollShadowBottom: any = document.querySelector(
        `.${classes.drawerScrollShadow}.${classes.drawerScrollShadowBottom}`,
      );
      drawerScrollShadowTop.style.opacity = scrollElement.scrollTop > 0 ? 1 : 0;
      drawerScrollShadowBottom.style.opacity =
        Math.round(scrollElement.scrollTop) +
          scrollElement.offsetHeight +
          offsetTop >=
        scrollElement.scrollHeight
          ? 0
          : 1;
    }
  };

  return (
    <BottomSheet
      className={`${clsx(classes.drawerContainer, {
        [classes.drawerOverlayContainer]: overlay,
        [classes.drawerWithFooter]: footer,
      })} slideNumber${idRef.current}
      `}
      ref={sheetRef}
      onSpringStart={event => {
        if (event.type === 'OPEN') {
          const currentPanelData = panel.find(p => p.id === idRef.current);

          if (!Boolean(currentPanelData)) {
            saveCurrentPanel();
          } else if (!Boolean(currentPanelData?.sheetRef.current)) {
            panel.splice(
              panel.findIndex(p => p.id === idRef.current),
              1,
            );
            saveCurrentPanel();
          }
          triggerCloseOtherPanelAndHideMainPanel();
          clearPreviousPanelData();
        }
        if (event.type === 'SNAP' && event.source === 'snap-to-bottom') {
          if (idRef.current !== 0) {
            closePanel();
          }
        }
        if (event.type === 'SNAP' && event.source === 'dragging') {
          if (idRef.current === 0) {
            clearPreviousPanelData();
          }
        }
        replaceNullDefaultPanel();
      }}
      open={open}
      onDismiss={onClose}
      blocking={overlay}
      snapPoints={({ minHeight, maxHeight }) =>
        footer
          ? [DEFAULT_MIN_HEIGHT, maxHeight - OFFSET_MAX_HEIGHT]
          : idRef.current === 0 || idRef.current === 7
          ? [
              DEFAULT_MIN_HEIGHT,
              minHeight > maxHeight - OFFSET_MAX_HEIGHT
                ? maxHeight - OFFSET_MAX_HEIGHT
                : minHeight,
              maxHeight - OFFSET_MAX_HEIGHT,
            ]
          : [minHeight]
      }
      defaultSnap={({
        lastSnap,
        snapPoints: defaultSnapPoints,
        minHeight,
        maxHeight,
      }) =>
        footer
          ? getDefaultSnapHeight(DrawerSnapType.maxHeight, minHeight, maxHeight)
          : idRef.current === 0 || idRef.current === 7
          ? getDefaultSnapHeight(defaultSnap, minHeight, maxHeight)
          : lastSnap ?? Math.max(...defaultSnapPoints)
      }
      footer={footer}
      onScroll={handleScroll}
    >
      <div
        className={`${classes.drawerScrollShadow} ${classes.drawerScrollShadowTop}`}
      ></div>
      <div
        className={`${classes.drawerScrollShadow} ${classes.drawerScrollShadowBottom}`}
      ></div>
      <Box component="div" className={classes.drawerContent}>
        {children}
      </Box>
    </BottomSheet>
  );
}
